import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    RecurringConfig,
    DEFAULT_RECURRING_CONFIG,
    RECURRING_LABELS,
    CalendarType,
    RecurrenceFrequency,
    getDefaultRecurringByEventType,
} from '../../types/recurringTypes';

interface RecurringToggleProps {
    config: RecurringConfig | undefined;
    eventType: string;
    onChange: (config: RecurringConfig) => void;
    className?: string;
}

const RecurringToggle: React.FC<RecurringToggleProps> = ({
    config,
    eventType,
    onChange,
    className = '',
}) => {
    const currentConfig: RecurringConfig = config || {
        ...DEFAULT_RECURRING_CONFIG,
        ...getDefaultRecurringByEventType(eventType),
    };

    const handleToggle = () => {
        const newConfig: RecurringConfig = {
            ...currentConfig,
            ...getDefaultRecurringByEventType(eventType),
            enabled: !currentConfig.enabled,
        };
        onChange(newConfig);
    };

    const handleCalendarTypeChange = (calendarType: CalendarType) => {
        onChange({ ...currentConfig, calendarType });
    };

    const handleFrequencyChange = (frequency: RecurrenceFrequency) => {
        onChange({ ...currentConfig, frequency });
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Toggle Button */}
            <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-gray-700">
                    Lặp lại sự kiện
                </label>
                <motion.button
                    type="button"
                    onClick={handleToggle}
                    whileTap={{ scale: 0.95 }}
                    className={`
                        relative w-12 h-6 rounded-full transition-colors
                        ${currentConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}
                    `}
                >
                    <motion.div
                        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow"
                        animate={{ left: currentConfig.enabled ? '1.5rem' : '0.25rem' }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                </motion.button>
            </div>

            {/* Expanded Options */}
            <AnimatePresence>
                {currentConfig.enabled && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="flex gap-3 pt-2 border-t border-gray-200">
                            {/* Calendar Type Dropdown */}
                            <div className="flex-1">
                                <label className="block text-xs font-semibold text-gray-600 mb-1">
                                    Lịch
                                </label>
                                <select
                                    value={currentConfig.calendarType}
                                    onChange={(e) => handleCalendarTypeChange(e.target.value as CalendarType)}
                                    className="w-full pl-3 pr-8 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B7280%22%20d%3D%22M2%204l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_0.75rem_center] bg-no-repeat cursor-pointer"
                                >
                                    {(['lunar', 'solar'] as CalendarType[]).map((type) => (
                                        <option key={type} value={type}>
                                            {RECURRING_LABELS.calendarType[type]}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Frequency Dropdown */}
                            <div className="flex-1">
                                <label className="block text-xs font-semibold text-gray-600 mb-1">
                                    Tần suất
                                </label>
                                <select
                                    value={currentConfig.frequency}
                                    onChange={(e) => handleFrequencyChange(e.target.value as RecurrenceFrequency)}
                                    className="w-full pl-3 pr-8 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236B7280%22%20d%3D%22M2%204l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_0.75rem_center] bg-no-repeat cursor-pointer"
                                >
                                    {(['daily', 'weekly', 'monthly', 'yearly'] as RecurrenceFrequency[]).map((freq) => (
                                        <option key={freq} value={freq}>
                                            {RECURRING_LABELS.frequency[freq]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {eventType === 'gio' && currentConfig.calendarType === 'lunar' && (
                            <p className="text-xs text-green-500 mt-2">
                                ✓ Khuyến nghị âm lịch cho ngày Giỗ
                            </p>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RecurringToggle;
